import React, { Fragment, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Animation from 'ls-common-client/dist/components/Animation';
import LayoutContainer from '../LayoutContainer';
import EmptyState from '../../assets/misc/no-data-empty-state.svg';
import useAssociativeArray from '../../hooks/useAssociativeArray';
import splitArray from '../../utils/splitArray';
import { Context } from '../../context/AppContext';
import listviewLoader from '../../animations/listviewLoader.json';
import './styles.css';

const alphabet = [
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'n',
  'o',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'y',
  'z',
];

const AlphaList = ({
  data,
  prepend = [],
  displayItem,
  loading,
  title,
  letter,
  fetchData,
  setData,
  emptyStateText,
}) => {
  const [show, setShow] = useState();
  const {
    media: { mobile, tablet, desktop },
  } = useContext(Context);

  const [items, addItem] = useAssociativeArray({});
  const selectedAlphabet = letter
    ? [...prepend, ...alphabet].indexOf(letter)
    : 0;
  const columns = 4;

  useEffect(() => {
    if (
      letter &&
      data &&
      (!items[letter] || (items[letter] && !items[letter].length))
    ) {
      addItem(letter, setData(data));
    }
  }, [data, letter]);

  const selectAlphabet = value => {
    fetchData({
      letter: value,
      isEmpty: items && !items[value],
    });
  };

  const emptyText = value => {
    return emptyStateText(value);
  };

  const mobileList = () => {
    const totalList =
      items[[...prepend, ...alphabet][Number(selectedAlphabet)]];
    const firstList = totalList.slice(0, 30);
    const moreList = totalList.slice(30, totalList.length);

    return (
      <Fragment>
        <div className="AlphaList__alphaList">
          <div className="AlphaList__alphaItems">
            {firstList.map((item, key) => (
              <Fragment key={key}>
                {displayItem ? displayItem(item) : item}
              </Fragment>
            ))}
          </div>
        </div>
        {show && moreList && !!moreList.length ? (
          <Fragment>
            <div className="AlphaList__alphaList">
              <div className="AlphaList__alphaItems">
                {moreList.map((item, key) => (
                  <Fragment key={key}>
                    {displayItem ? displayItem(item) : item}
                  </Fragment>
                ))}
              </div>
            </div>
            <button onClick={setShow} className="AlphaList__toggleShow">
              View Less <i className="icon-generalcaretupmedium" />
            </button>
          </Fragment>
        ) : (
          ''
        )}
        {!show && moreList && !!moreList.length ? (
          <button onClick={setShow} className="AlphaList__toggleShow">
            View More <i className="icon-generalcaretdownmedium" />
          </button>
        ) : (
          ''
        )}
      </Fragment>
    );
  };

  return (
    <LayoutContainer>
      <div className="AlphaList__pagination">
        {title ? <div className="AlphaList__listTitle">{title}</div> : ''}
        <div className="AlphaList__alphabetWrapper">
          <div className="AlphaList__alphabetList">
            {[...prepend, ...alphabet].map((item, key) => (
              <button
                className={`AlphaList__pointer ${
                  key === selectedAlphabet ? 'active' : ''
                }`}
                key={key}
                onClick={
                  /* istanbul ignore next: necessary anonymous function */ () =>
                    selectAlphabet(item, key)
                }
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="AlphaList__loadingState">
            <Animation data={listviewLoader} height="200px" width="250px" />
          </div>
        ) : (
          ''
        )}
        {!loading && mobile ? (
          <Fragment>
            {items &&
            items[[...prepend, ...alphabet][Number(selectedAlphabet)]] &&
            !!items[[...prepend, ...alphabet][Number(selectedAlphabet)]]
              .length ? (
              mobileList()
            ) : (
              <div className="AlphaList__emptyState">
                <img src={EmptyState} alt="empty list" />
                <div>
                  {emptyText(
                    [...prepend, ...alphabet][Number(selectedAlphabet)]
                  )}
                </div>
              </div>
            )}
          </Fragment>
        ) : null}
        {!loading && (desktop || tablet) ? (
          <Fragment>
            {items &&
            items[[...prepend, ...alphabet][Number(selectedAlphabet)]] &&
            !!items[[...prepend, ...alphabet][Number(selectedAlphabet)]]
              .length ? (
              <div className="AlphaList__alphaList">
                {splitArray(
                  items[[...prepend, ...alphabet][Number(selectedAlphabet)]],
                  columns
                ).map((item, key) => (
                  <div key={key} className="AlphaList__alphaItems">
                    {item &&
                      !!item.length &&
                      item.map((item2, key2) => (
                        <Fragment key={key2}>
                          {displayItem ? displayItem(item2) : item2}
                        </Fragment>
                      ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="AlphaList__emptyState">
                <img src={EmptyState} alt="empty list" />
                <div>
                  {emptyText(
                    [...prepend, ...alphabet][Number(selectedAlphabet)]
                  )}
                </div>
              </div>
            )}
          </Fragment>
        ) : null}
      </div>
    </LayoutContainer>
  );
};

AlphaList.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.arrayOf(PropTypes.shape({})),
  ]),
  displayItem: PropTypes.func,
  letter: PropTypes.string,
  title: PropTypes.string,
  emptyStateText: PropTypes.func,
  setData: PropTypes.func,
  fetchData: PropTypes.func,
  loading: PropTypes.bool,
};

AlphaList.defaultProps = {
  data: undefined,
  displayItem: () => {},
  letter: undefined,
  title: undefined,
  emptyStateText: () => {},
  setData: () => {},
  fetchData: () => {},
  loading: false,
};

export default AlphaList;
